import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import {
  Inbox, Mail, MailOpen, Star, AlertOctagon, Archive, Trash2, LogOut,
  Image as ImageIcon, KeyRound, Loader2, Upload, X, RefreshCw, Check,
  CalendarDays, PartyPopper, ArchiveRestore, Plus, Lock, Ban,
} from "lucide-react";
import { useAdmin } from "../admin/AdminAuth";
import { resolveUrl } from "../context/MediaContext";
import { useToast } from "../hooks/use-toast";

const VIEWS = [
  { key: "inbox", label: "Inbox", icon: Inbox },
  { key: "unread", label: "Unread", icon: Mail },
  { key: "important", label: "Important", icon: Star },
  { key: "spam", label: "Spam", icon: AlertOctagon },
  { key: "archived", label: "Archived", icon: Archive },
];

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export default function AdminDashboard() {
  const { admin, logout, changePassword, authHeader, API } = useAdmin();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState(admin?.must_change_password ? "account" : "inbox");

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="admin-area min-h-screen bg-[#F1EEE6]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-[#0A3B2E] text-white sticky top-0 z-20">
        <div className="max-w-[1300px] mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-display font-black text-xl">TJH</span>
            <span className="text-white/50 text-xs tracking-widest uppercase">Admin</span>
          </div>
          <nav className="flex items-center gap-1">
            {[{ k: "inbox", l: "Inbox", i: Inbox }, { k: "calendar", l: "Calendar", i: CalendarDays }, { k: "photos", l: "Photos", i: ImageIcon }, { k: "account", l: "Account", i: KeyRound }].map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.k ? "bg-[var(--moss)] text-white" : "text-white/70 hover:text-white"}`}>
                <t.i size={16} /> <span className="hidden sm:inline">{t.l}</span>
              </button>
            ))}
            <button onClick={doLogout} data-testid="admin-logout" className="ml-2 flex items-center gap-2 px-3 py-2 rounded-full text-sm text-white/70 hover:text-white">
              <LogOut size={16} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </nav>
        </div>
      </header>

      {admin?.must_change_password && tab !== "account" && (
        <div className="bg-amber-100 text-amber-900 text-sm text-center py-2 px-4" data-testid="admin-change-pw-banner">
          For security, please change your temporary password in the <button onClick={() => setTab("account")} className="underline font-semibold">Account</button> tab.
        </div>
      )}

      <main className="max-w-[1300px] mx-auto px-5 py-6">
        {tab === "inbox" && <InboxView authHeader={authHeader} API={API} toast={toast} />}
        {tab === "calendar" && <CalendarView authHeader={authHeader} API={API} toast={toast} />}
        {tab === "photos" && <PhotosView authHeader={authHeader} API={API} toast={toast} />}
        {tab === "account" && <AccountView admin={admin} changePassword={changePassword} toast={toast} />}
      </main>
    </div>
  );
}

/* ---------------- INBOX ---------------- */
function InboxView({ authHeader, API, toast }) {
  const [view, setView] = useState("inbox");
  const [data, setData] = useState({ items: [], counts: {} });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (v = view) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/inbox?view=${v}`, authHeader());
      setData(data);
    } catch { toast({ title: "Couldn't load inbox" }); }
    finally { setLoading(false); }
  }, [API, authHeader, view, toast]);

  useEffect(() => { load(view); /* eslint-disable-next-line */ }, [view]);

  const patch = async (item, changes) => {
    try {
      await axios.patch(`${API}/admin/inbox/${item.type}/${item.id}`, changes, authHeader());
      await load(view);
      setSelected((s) => (s && s.id === item.id ? { ...s, ...changes } : s));
    } catch { toast({ title: "Action failed" }); }
  };

  const remove = async (item) => {
    try {
      await axios.delete(`${API}/admin/inbox/${item.type}/${item.id}`, authHeader());
      setSelected(null);
      await load(view);
      toast({ title: "Deleted" });
    } catch { toast({ title: "Delete failed" }); }
  };

  const open = (item) => {
    setSelected(item);
    if (!item.read) patch(item, { read: true });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
      {/* views sidebar */}
      <aside className="lg:col-span-3">
        <div className="bg-white rounded-2xl p-2 shadow-sm">
          {VIEWS.map((v) => (
            <button key={v.key} onClick={() => { setView(v.key); setSelected(null); }} data-testid={`inbox-view-${v.key}`}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-colors ${view === v.key ? "bg-[#0A3B2E] text-white" : "text-[#16241E]/80 hover:bg-[#0A3B2E]/5"}`}>
              <span className="flex items-center gap-2.5"><v.icon size={16} /> {v.label}</span>
              {data.counts[v.key] > 0 && (
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${view === v.key ? "bg-white/20" : "bg-[var(--moss)]/15 text-[var(--moss-deep)]"}`}>{data.counts[v.key]}</span>
              )}
            </button>
          ))}
          <button onClick={() => load(view)} className="w-full flex items-center gap-2 px-3 py-2.5 mt-1 rounded-xl text-sm text-[#16241E]/50 hover:bg-[#0A3B2E]/5" data-testid="inbox-refresh">
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </aside>

      {/* list */}
      <section className="lg:col-span-4">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden min-h-[300px]">
          {loading ? (
            <div className="p-8 text-center text-[#16241E]/40"><Loader2 className="animate-spin mx-auto" /></div>
          ) : data.items.length === 0 ? (
            <div className="p-10 text-center text-[#16241E]/40 text-sm">Nothing here.</div>
          ) : (
            <ul data-testid="inbox-list">
              {data.items.map((it) => (
                <li key={`${it.type}-${it.id}`}>
                  <button onClick={() => open(it)} data-testid={`inbox-item-${it.id}`}
                    className={`w-full text-left px-4 py-3.5 border-b border-[#0A3B2E]/8 hover:bg-[#0A3B2E]/[0.03] transition-colors ${selected?.id === it.id ? "bg-[#0A3B2E]/[0.05]" : ""} ${!it.read ? "font-semibold" : ""}`}>
                    <div className="flex items-center gap-2">
                      {!it.read && <span className="w-2 h-2 rounded-full bg-[var(--moss)] shrink-0" />}
                      {it.important && <Star size={13} className="fill-amber-400 text-amber-400 shrink-0" />}
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${it.type === "booking" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"}`}>
                        {it.type === "booking" ? <CalendarDays size={11} /> : <PartyPopper size={11} />}{it.type}
                      </span>
                      <span className="ml-auto text-[11px] text-[#16241E]/40 font-normal">{fmtDate(it.created_at)}</span>
                    </div>
                    <p className="mt-1 text-[#0A3B2E] truncate">{it.name || "(no name)"}</p>
                    <p className="text-xs text-[#16241E]/55 font-normal truncate">{it.subject}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* detail */}
      <section className="lg:col-span-5">
        <div className="bg-white rounded-2xl shadow-sm p-6 min-h-[300px]" data-testid="inbox-detail">
          {!selected ? (
            <div className="h-full grid place-items-center text-[#16241E]/40 text-sm py-20">Select a message to read it.</div>
          ) : (
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <button onClick={() => patch(selected, { important: !selected.important })} data-testid="detail-important" title="Important" className={`p-2 rounded-lg border ${selected.important ? "bg-amber-50 border-amber-300 text-amber-600" : "border-[#0A3B2E]/15 text-[#16241E]/60 hover:bg-[#0A3B2E]/5"}`}><Star size={16} className={selected.important ? "fill-amber-400" : ""} /></button>
                <button onClick={() => patch(selected, { read: !selected.read })} data-testid="detail-read" title="Toggle read" className="p-2 rounded-lg border border-[#0A3B2E]/15 text-[#16241E]/60 hover:bg-[#0A3B2E]/5">{selected.read ? <Mail size={16} /> : <MailOpen size={16} />}</button>
                <button onClick={() => patch(selected, { spam: !selected.spam })} data-testid="detail-spam" title="Spam" className={`p-2 rounded-lg border ${selected.spam ? "bg-red-50 border-red-300 text-red-600" : "border-[#0A3B2E]/15 text-[#16241E]/60 hover:bg-[#0A3B2E]/5"}`}><AlertOctagon size={16} /></button>
                <button onClick={() => patch(selected, { archived: !selected.archived })} data-testid="detail-archive" title="Archive" className="p-2 rounded-lg border border-[#0A3B2E]/15 text-[#16241E]/60 hover:bg-[#0A3B2E]/5">{selected.archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}</button>
                <button onClick={() => remove(selected)} data-testid="detail-delete" title="Delete" className="p-2 rounded-lg border border-[#0A3B2E]/15 text-red-500 hover:bg-red-50 ml-auto"><Trash2 size={16} /></button>
              </div>

              <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${selected.type === "booking" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"}`}>
                {selected.type}
              </span>
              <h3 className="font-display font-bold text-2xl text-[#0A3B2E] mt-2">{selected.name}</h3>
              <p className="text-[#16241E]/50 text-sm">{fmtDate(selected.created_at)}</p>

              <div className="mt-5 space-y-2.5 text-sm">
                <Row label="Email"><a href={`mailto:${selected.email}`} className="text-[var(--moss-deep)] underline">{selected.email}</a></Row>
                {selected.phone && <Row label="Phone"><a href={`tel:${selected.phone}`} className="text-[var(--moss-deep)]">{selected.phone}</a></Row>}
                {selected.type === "booking" ? (
                  <>
                    <Row label="Check-in">{selected.checkin}</Row>
                    <Row label="Check-out">{selected.checkout}</Row>
                    <Row label="Nights">{selected.nights}</Row>
                    <Row label="Guests">{selected.guests}</Row>
                  </>
                ) : (
                  <>
                    <Row label="Event">{selected.event_type}</Row>
                    {selected.event_date && <Row label="Date">{selected.event_date}</Row>}
                    <Row label="Guests">{selected.guests}</Row>
                  </>
                )}
                {selected.message && (
                  <div className="pt-3">
                    <p className="text-[#16241E]/45 text-xs uppercase tracking-wide mb-1">Message</p>
                    <p className="text-[#16241E]/80 bg-[#0A3B2E]/[0.04] rounded-xl p-3 whitespace-pre-wrap">{selected.message}</p>
                  </div>
                )}
              </div>
              <a href={`https://wa.me/?text=${encodeURIComponent(`Hi ${selected.name}, thanks for reaching out to The Jackson House!`)}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 mt-6 sunset-fill text-white text-sm font-semibold px-5 py-2.5 rounded-full">
                Reply via WhatsApp
              </a>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div className="flex gap-3"><span className="w-24 shrink-0 text-[#16241E]/45">{label}</span><span className="text-[#16241E]/85">{children}</span></div>
);

/* ---------------- CALENDAR (manual date blocking) ---------------- */
function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fromISO(iso) {
  return new Date(`${iso}T00:00:00`);
}
function expandRange(range) {
  if (!range?.from) return [];
  const end = range.to || range.from;
  const out = [];
  const cur = new Date(range.from);
  cur.setHours(0, 0, 0, 0);
  const last = new Date(end);
  last.setHours(0, 0, 0, 0);
  while (cur <= last) {
    out.push(toISO(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function CalendarView({ authHeader, API, toast }) {
  const [manual, setManual] = useState([]);
  const [airbnb, setAirbnb] = useState([]);
  const [range, setRange] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blocked-dates`, authHeader());
      setManual(data.manual || []);
      setAirbnb(data.airbnb || []);
    } catch { toast({ title: "Couldn't load calendar" }); }
    finally { setLoading(false); }
  }, [API, authHeader, toast]);

  useEffect(() => { load(); }, [load]);

  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
  const airbnbDates = useMemo(() => airbnb.map(fromISO), [airbnb]);
  const manualDates = useMemo(() => manual.map(fromISO), [manual]);

  const selectedISO = useMemo(() => expandRange(range), [range]);
  const toBlock = selectedISO.filter((d) => !manual.includes(d) && !airbnb.includes(d));
  const toUnblock = selectedISO.filter((d) => manual.includes(d));

  const block = async () => {
    if (!toBlock.length) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/blocked-dates`, { dates: toBlock }, authHeader());
      setManual(data.manual || []);
      setRange(undefined);
      toast({ title: `Blocked ${toBlock.length} date${toBlock.length > 1 ? "s" : ""}`, description: "Now unavailable on the website." });
    } catch { toast({ title: "Couldn't block dates" }); }
    finally { setBusy(false); }
  };

  const unblock = async (dates) => {
    if (!dates.length) return;
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/admin/blocked-dates`, { ...authHeader(), data: { dates } });
      setManual(data.manual || []);
      setRange(undefined);
      toast({ title: `Unblocked ${dates.length} date${dates.length > 1 ? "s" : ""}` });
    } catch { toast({ title: "Couldn't unblock dates" }); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 text-center text-[#16241E]/40" data-testid="calendar-loading"><Loader2 className="animate-spin mx-auto" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5" data-testid="calendar-view">
      {/* calendar picker */}
      <section className="lg:col-span-7">
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="font-display font-bold text-xl text-[#0A3B2E]">Block off dates</h3>
          <p className="text-sm text-[#16241E]/55 mt-1">Click a day, or click a start and end day to select a range — then block or unblock it. Blocked dates are hidden from the website booking calendar.</p>

          <div className="mt-4 tjh-admin-cal">
            <DayPicker
              mode="range"
              selected={range}
              onSelect={setRange}
              numberOfMonths={1}
              disabled={[{ before: today }, ...airbnbDates]}
              modifiers={{ manualBlocked: manualDates, airbnbBlocked: airbnbDates }}
              modifiersClassNames={{
                manualBlocked: "tjh-manual-blocked",
                airbnbBlocked: "tjh-airbnb-blocked",
              }}
              data-testid="admin-calendar"
            />
          </div>

          {/* legend */}
          <div className="flex flex-wrap gap-4 mt-4 text-xs text-[#16241E]/60">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[var(--moss)]/25 border border-[var(--moss)]/50" /> Your block</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#0A3B2E]/10 border border-[#0A3B2E]/25" /><Lock size={12} /> Airbnb (locked)</span>
          </div>

          {/* actions */}
          {selectedISO.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 mt-5 pt-4 border-t border-[#0A3B2E]/10" data-testid="calendar-actions">
              <span className="text-sm text-[#16241E]/70">{selectedISO.length} day{selectedISO.length > 1 ? "s" : ""} selected</span>
              <div className="ml-auto flex gap-2">
                <button onClick={() => setRange(undefined)} className="text-sm px-4 py-2 rounded-full border border-[#0A3B2E]/15 text-[#16241E]/70 hover:bg-[#0A3B2E]/5" data-testid="calendar-clear">Clear</button>
                {toUnblock.length > 0 && (
                  <button onClick={() => unblock(toUnblock)} disabled={busy} data-testid="calendar-unblock-btn"
                    className="text-sm px-4 py-2 rounded-full border border-[var(--moss-deep)]/30 text-[var(--moss-deep)] font-semibold hover:bg-[var(--moss)]/10 disabled:opacity-60">
                    Unblock {toUnblock.length}
                  </button>
                )}
                <button onClick={block} disabled={busy || !toBlock.length} data-testid="calendar-block-btn"
                  className="inline-flex items-center gap-1.5 text-sm px-5 py-2 rounded-full sunset-fill text-white font-semibold disabled:opacity-50">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Ban size={15} />} Block {toBlock.length || ""}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* current blocks list */}
      <aside className="lg:col-span-5">
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-xl text-[#0A3B2E]">Your blocked dates</h3>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[var(--moss)]/15 text-[var(--moss-deep)]">{manual.length}</span>
          </div>
          {manual.length === 0 ? (
            <p className="text-sm text-[#16241E]/45 mt-4">No manual blocks yet. Select dates on the calendar to block them.</p>
          ) : (
            <ul className="mt-4 space-y-1.5 max-h-[420px] overflow-auto pr-1" data-testid="blocked-dates-list">
              {manual.map((d) => (
                <li key={d} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#0A3B2E]/[0.04]" data-testid={`blocked-date-${d}`}>
                  <span className="text-sm text-[#16241E]/85">{fromISO(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</span>
                  <button onClick={() => unblock([d])} disabled={busy} title="Unblock" data-testid={`remove-block-${d}`}
                    className="p-1.5 rounded-lg text-[#16241E]/50 hover:bg-red-50 hover:text-red-500 disabled:opacity-50"><X size={15} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

/* ---------------- PHOTOS ---------------- */
function PhotosView({ authHeader, API, toast }) {
  const [media, setMedia] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/media`, authHeader()); setMedia(data); }
    catch { toast({ title: "Couldn't load photos" }); }
  }, [API, authHeader, toast]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await axios.post(`${API}/admin/media/upload`, fd, { ...authHeader() });
    return data.url;
  };

  const replaceSlot = async (key, file) => {
    if (!file) return;
    setBusyKey(key);
    try {
      const url = await upload(file);
      await axios.put(`${API}/admin/media/slot`, { key, url }, authHeader());
      await load();
      toast({ title: "Photo updated", description: "Refresh the website to see it live." });
    } catch (e) { toast({ title: "Upload failed", description: e?.response?.data?.detail || "" }); }
    finally { setBusyKey(null); }
  };

  const addEvent = async (files) => {
    setAdding(true);
    try {
      for (const f of files) {
        const url = await upload(f);
        await axios.post(`${API}/admin/media/event`, { url }, authHeader());
      }
      await load();
      toast({ title: "Added to event gallery" });
    } catch (e) { toast({ title: "Upload failed", description: e?.response?.data?.detail || "" }); }
    finally { setAdding(false); }
  };

  const removeEvent = async (url) => {
    try { const { data } = await axios.delete(`${API}/admin/media/event`, { ...authHeader(), data: { url } }); setMedia((m) => ({ ...m, event_gallery: data.event_gallery })); }
    catch { toast({ title: "Remove failed" }); }
  };

  if (!media) return <div className="p-10 text-center text-[#16241E]/40"><Loader2 className="animate-spin mx-auto" /></div>;

  const groups = [...new Set(media.slot_meta.map((s) => s.group))];

  return (
    <div className="space-y-8" data-testid="photos-view">
      {groups.map((g) => (
        <div key={g}>
          <h3 className="font-display font-bold text-xl text-[#0A3B2E] mb-3">{g}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {media.slot_meta.filter((s) => s.group === g).map((s) => (
              <div key={s.key} className="bg-white rounded-2xl overflow-hidden shadow-sm" data-testid={`slot-${s.key}`}>
                <div className="relative aspect-[4/3] bg-[#0A3B2E]/5">
                  <img src={resolveUrl(media.slots[s.key])} alt={s.label} className="w-full h-full object-cover" />
                  {busyKey === s.key && <div className="absolute inset-0 bg-black/40 grid place-items-center"><Loader2 className="animate-spin text-white" /></div>}
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold text-[#0A3B2E] truncate">{s.label}</p>
                  <label className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--moss-deep)] cursor-pointer hover:underline" data-testid={`replace-${s.key}`}>
                    <Upload size={13} /> Replace
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => replaceSlot(s.key, e.target.files[0])} />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Event gallery */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-xl text-[#0A3B2E]">Event Gallery ({media.event_gallery.length})</h3>
          <label className="inline-flex items-center gap-2 sunset-fill text-white text-sm font-semibold px-4 py-2 rounded-full cursor-pointer" data-testid="event-add">
            {adding ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add Photos
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addEvent(Array.from(e.target.files))} />
          </label>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3" data-testid="event-gallery-grid">
          {media.event_gallery.map((url, i) => (
            <div key={url + i} className="relative group rounded-xl overflow-hidden aspect-square bg-[#0A3B2E]/5">
              <img src={resolveUrl(url)} alt={`Event ${i + 1}`} className="w-full h-full object-cover" />
              <button onClick={() => removeEvent(url)} data-testid={`event-remove-${i}`}
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/55 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500">
                <X size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- ACCOUNT ---------------- */
function AccountView({ admin, changePassword, toast }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw !== cf) { toast({ title: "Passwords don't match" }); return; }
    if (nw.length < 8) { toast({ title: "Password must be at least 8 characters" }); return; }
    setBusy(true);
    try {
      await changePassword(cur, nw);
      setOk(true); setCur(""); setNw(""); setCf("");
      toast({ title: "Password changed" });
    } catch (e) { toast({ title: "Change failed", description: e?.response?.data?.detail || "" }); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-[#0A3B2E]/15 focus:outline-none focus:ring-2 focus:ring-[var(--moss)]/40";

  return (
    <div className="max-w-md" data-testid="account-view">
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h3 className="font-display font-bold text-xl text-[#0A3B2E]">Account</h3>
        <p className="text-sm text-[#16241E]/55 mt-1">{admin?.email}</p>
        <form onSubmit={submit} className="mt-5 space-y-3" data-testid="change-password-form">
          <input type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} className={inputCls} data-testid="cur-password" required />
          <input type="password" placeholder="New password" value={nw} onChange={(e) => setNw(e.target.value)} className={inputCls} data-testid="new-password" required />
          <input type="password" placeholder="Confirm new password" value={cf} onChange={(e) => setCf(e.target.value)} className={inputCls} data-testid="confirm-password" required />
          <button type="submit" disabled={busy} className="w-full flex items-center justify-center gap-2 sunset-fill text-white font-bold py-3 rounded-xl disabled:opacity-70" data-testid="change-password-submit">
            {busy ? <Loader2 size={18} className="animate-spin" /> : ok ? <Check size={18} /> : <KeyRound size={18} />} Update Password
          </button>
        </form>
      </div>
    </div>
  );
}
